import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { useReferenceOptions } from '@/lib/use-reference-options';
import { Button, ConfirmDialog, Field, Input, Modal, MultiCombobox } from '@/components/ui';
import type { MultiComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import type {
  City,
  ClientDeliverable,
  ClientDeliverablePieceInput,
  ClientDeliverablePieceTarget,
} from '@/lib/types';

/**
 * SPLITTING ONE TRACKER ROW INTO PIECES — `PUT /client-deliverables/pieces`.
 *
 * Pieces DIVIDE the row's promised quantity and must add up to it exactly; the
 * server refuses anything else, so Save stays disabled until they do and says
 * why beside the button. A piece may carry 0 (a quantity-1 line splits 1 + 0).
 *
 * ⚠️ **The write is a WHOLE-SET REPLACE.** Every piece is sent every time, with
 * its `id` when it already exists, so an omitted piece is a DELETED piece.
 * `pieces: []` is the unsplit, which is why it sits behind a confirmation.
 *
 * ⚠️ **Field errors index into the array that was SENT.** Nothing here filters
 * the drafts before sending, so `pieces.N.promised` is the draft at index N —
 * the `ccEmails` defect (a filtered payload, an unfiltered screen, a refusal
 * marking the wrong control) cannot arise.
 *
 * Only the PER-PIECE fields live here (promised, completed, dates, cities,
 * regions). The shared ones — POC, specialist, booking spec and the rest — stay
 * on the row's overlay and are edited in the table, where editing one piece
 * edits every piece.
 */

export interface SplitDeliverableModalProps {
  open: boolean;
  row: ClientDeliverable | null;
  /** Every row on screen sharing the same target, `row` included. */
  siblings: ClientDeliverable[];
  onClose: () => void;
  /** The page refetches: a split changes how many rows the target renders. */
  onSaved: () => void;
}

const MAX_PIECES = 50;
const CITIES_PER_PAGE = 200;

type PieceField = 'promised' | 'completed' | 'startDate' | 'endDate' | 'cityIds' | 'regionIds';

interface Draft {
  /** React key and error key — survives removal of a neighbour. */
  localKey: number;
  /** The existing piece id, sent back so the server keeps the row. */
  id?: number;
  promised: string;
  completed: string;
  startDate: string;
  endDate: string;
  cityIds: string[];
  regionIds: string[];
}

const WHOLE = /^\d+$/;

function targetOf(row: ClientDeliverable): ClientDeliverablePieceTarget | null {
  if (row.line?.kind === 'quote') return { quoteProductId: row.line.id };
  if (row.line?.kind === 'invoice') return { invoiceProductId: row.line.id };
  if (row.source === 'standalone' && row.id !== null) return { clientDeliverableId: row.id };
  return null;
}

/** A blank or non-integer box parses to null, never to 0. */
function wholeOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  return WHOLE.test(trimmed) ? Number(trimmed) : null;
}

function initialDrafts(row: ClientDeliverable, siblings: ClientDeliverable[]): Draft[] {
  let next = 1;
  const pieceRows = siblings
    .filter((sibling) => sibling.pieceId !== null && sibling.piece !== null)
    .sort((a, b) => (a.piece?.position ?? 0) - (b.piece?.position ?? 0));
  const existing = pieceRows.length > 0 ? pieceRows : row.piece !== null ? [row] : [];

  if (existing.length > 0) {
    return existing.map((piece) => ({
      localKey: next++,
      id: piece.pieceId ?? undefined,
      promised: String(piece.promised),
      completed: String(piece.completed),
      startDate: piece.startDate ?? '',
      endDate: piece.endDate ?? '',
      // The piece's OWN sets only. `cities` / `regions` on the row merge in the
      // row set when the piece names none, and sending that merge back would
      // freeze the row set onto the piece.
      cityIds: (piece.piece?.ownCityIds ?? []).map(String),
      regionIds: (piece.piece?.ownRegionIds ?? []).map(String),
    }));
  }

  // A first split: piece 1 carries the row as it stands, piece 2 starts empty.
  return [
    {
      localKey: next++,
      promised: String(row.promised),
      completed: String(row.completed),
      startDate: row.startDate ?? '',
      endDate: row.endDate ?? '',
      // Empty = inherit the row's set, which is what piece 1 shows until
      // somebody narrows it.
      cityIds: [],
      regionIds: [],
    },
    {
      localKey: next++,
      promised: '0',
      completed: '0',
      startDate: '',
      endDate: '',
      cityIds: [],
      regionIds: [],
    },
  ];
}

export function SplitDeliverableModal(props: SplitDeliverableModalProps) {
  const { open, row } = props;
  if (!open || row === null) return null;
  // Keyed on the row, so a different row always starts from its own values
  // rather than from whatever the previous dialog left in state.
  return <SplitBody key={row.key} {...props} row={row} />;
}

function SplitBody({
  row,
  siblings,
  onClose,
  onSaved,
}: SplitDeliverableModalProps & { row: ClientDeliverable }) {
  const total = row.piece?.rowPromised ?? row.promised;
  const alreadySplit = row.piece !== null || siblings.some((sibling) => sibling.pieceId !== null);
  const target = targetOf(row);

  const [drafts, setDrafts] = useState<Draft[]>(() => initialDrafts(row, siblings));
  const nextKey = useRef(drafts.reduce((max, draft) => Math.max(max, draft.localKey), 0) + 1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmUnsplit, setConfirmUnsplit] = useState(false);
  const [unsplitError, setUnsplitError] = useState<string | null>(null);

  /* ---- cities: active only, one page at the API ceiling ---------------- */
  const [cities, setCities] = useState<City[] | null>(null);
  const [citiesFailed, setCitiesFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listCities({ isActive: true, perPage: CITIES_PER_PAGE }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCities(result.data);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setCities([]);
        setCitiesFailed(true);
      });
    return () => controller.abort();
  }, []);

  const regions = useReferenceOptions('regions');

  /*
   * A stored city or region the active list does not carry (retired, or past
   * the page) is injected back under the name the row was read with, so a
   * piece never renders a chip as a bare id or silently drops it on Save.
   */
  const storedCityNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const sibling of [row, ...siblings]) {
      for (const city of sibling.cities) names.set(String(city.id), city.name);
    }
    return names;
  }, [row, siblings]);
  const storedRegionNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const sibling of [row, ...siblings]) {
      for (const region of sibling.regions) names.set(String(region.id), region.name);
    }
    return names;
  }, [row, siblings]);

  const cityOptions = useMemo<MultiComboboxOption[]>(() => {
    const options: MultiComboboxOption[] = (cities ?? []).map((city) => ({
      value: String(city.id),
      label: city.name,
      ...(city.country ? { hint: city.country.name } : {}),
    }));
    const offered = new Set(options.map((option) => option.value));
    for (const [value, label] of storedCityNames) {
      if (!offered.has(value)) options.push({ value, label });
    }
    return options;
  }, [cities, storedCityNames]);

  const regionOptions = useMemo<MultiComboboxOption[]>(() => {
    const options: MultiComboboxOption[] = regions.records.map((record) => ({
      value: String(record.id),
      label: record.name,
    }));
    const offered = new Set(options.map((option) => option.value));
    for (const [value, label] of storedRegionNames) {
      if (!offered.has(value)) options.push({ value, label });
    }
    return options;
  }, [regions.records, storedRegionNames]);

  /* ---- arithmetic ----------------------------------------------------- */
  const allocated = drafts.reduce((sum, draft) => sum + (wholeOrNull(draft.promised) ?? 0), 0);
  const allQuantitiesValid = drafts.every((draft) => wholeOrNull(draft.promised) !== null);

  const blockedReason: string | null =
    target === null
      ? 'This row has no line or record a split can attach to.'
      : drafts.length < 2
        ? 'A split needs at least two pieces.'
        : !allQuantitiesValid
          ? 'Every piece needs a whole-number quantity.'
          : allocated !== total
            ? `The pieces add up to ${allocated}, and they must add up to ${total}.`
            : null;

  function setField(localKey: number, field: Exclude<PieceField, 'cityIds' | 'regionIds'>, value: string): void;
  function setField(localKey: number, field: 'cityIds' | 'regionIds', value: string[]): void;
  function setField(localKey: number, field: PieceField, value: string | string[]) {
    setDrafts((prev) =>
      prev.map((draft) => (draft.localKey === localKey ? { ...draft, [field]: value } : draft)),
    );
    setErrors((prev) => {
      const key = `${localKey}.${field}`;
      const startKey = `${localKey}.endDate`;
      if (!(key in prev) && !(field === 'startDate' && startKey in prev)) return prev;
      const copy = { ...prev };
      delete copy[key];
      // The order rule is reported on the end date, so moving the start clears it.
      if (field === 'startDate') delete copy[startKey];
      return copy;
    });
  }

  function addPiece() {
    setDrafts((prev) =>
      prev.length >= MAX_PIECES
        ? prev
        : [
            ...prev,
            {
              localKey: nextKey.current++,
              promised: '0',
              completed: '0',
              startDate: '',
              endDate: '',
              cityIds: [],
              regionIds: [],
            },
          ],
    );
  }

  /** The removed quantity goes back to the first remaining piece. */
  function removePiece(localKey: number) {
    setDrafts((prev) => {
      const removed = prev.find((draft) => draft.localKey === localKey);
      const rest = prev.filter((draft) => draft.localKey !== localKey);
      const first = rest[0];
      if (!removed || first === undefined) return prev;
      const handBack = wholeOrNull(removed.promised) ?? 0;
      const firstQty = wholeOrNull(first.promised);
      const merged = firstQty === null ? first.promised : String(firstQty + handBack);
      return [{ ...first, promised: merged }, ...rest.slice(1)];
    });
    setErrors((prev) => {
      const copy: Record<string, string> = {};
      for (const [key, message] of Object.entries(prev)) {
        if (!key.startsWith(`${localKey}.`)) copy[key] = message;
      }
      return copy;
    });
  }

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};
    for (const draft of drafts) {
      if (wholeOrNull(draft.promised) === null) {
        found[`${draft.localKey}.promised`] = 'Enter a whole number, 0 or more.';
      }
      if (wholeOrNull(draft.completed) === null) {
        found[`${draft.localKey}.completed`] = 'Enter a whole number, 0 or more. Use 0 for nothing delivered yet.';
      }
      if (draft.startDate && draft.endDate && draft.startDate > draft.endDate) {
        found[`${draft.localKey}.endDate`] = 'The end date cannot be before the start date.';
      }
    }
    return found;
  }

  /** Routes a refusal's `pieces.N.<field>` paths onto the draft at index N of what was SENT. */
  function applyRefusal(caught: unknown, sent: Draft[]): string | null {
    if (!(caught instanceof ApiError)) {
      return 'Could not reach the server. Check your connection.';
    }
    if (caught.status === 403) return `${caught.message} Nothing was changed.`;

    const routed: Record<string, string> = {};
    const banner: string[] = [...caught.formMessages];
    for (const [path, message] of Object.entries(caught.fieldErrors)) {
      const match = /^pieces\.(\d+)\.(promised|completed|startDate|endDate|cityIds|regionIds)\b/.exec(path);
      const draft = match ? sent[Number(match[1])] : undefined;
      if (match && draft) routed[`${draft.localKey}.${match[2]}`] = message;
      else banner.push(message);
    }
    setErrors(routed);
    if (banner.length > 0) return banner.join(' ');
    return Object.keys(routed).length === 0 ? caught.message : null;
  }

  async function save() {
    if (target === null || blockedReason !== null || saving) return;
    const found = validate();
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    const sent = drafts;
    const pieces: ClientDeliverablePieceInput[] = sent.map((draft) => ({
      ...(draft.id !== undefined ? { id: draft.id } : {}),
      promised: Number(draft.promised.trim()),
      completed: Number(draft.completed.trim()),
      startDate: draft.startDate === '' ? null : draft.startDate,
      endDate: draft.endDate === '' ? null : draft.endDate,
      cityIds: draft.cityIds.map(Number),
      regionIds: draft.regionIds.map(Number),
    }));

    setSaving(true);
    try {
      await api.replaceClientDeliverablePieces({ target, pieces });
      onSaved();
      onClose();
    } catch (caught) {
      setFormError(applyRefusal(caught, sent));
      setSaving(false);
    }
  }

  /*
   * The server sums the STORED completed counts, so the dialog states those —
   * never the drafts on screen, which may hold edits nobody has saved.
   */
  const storedPieces = siblings.filter((sibling) => sibling.pieceId !== null);
  const completedSum = storedPieces.reduce((sum, piece) => sum + piece.completed, 0);
  const storedPieceCount = storedPieces.length;

  async function unsplit() {
    if (target === null || saving) return;
    setSaving(true);
    setUnsplitError(null);
    try {
      await api.replaceClientDeliverablePieces({ target, pieces: [] });
      setConfirmUnsplit(false);
      onSaved();
      onClose();
    } catch (caught) {
      setUnsplitError(
        caught instanceof ApiError
          ? (caught.formMessage ?? caught.message)
          : 'Could not reach the server. Check your connection.',
      );
      setSaving(false);
    }
  }

  const rowName = [row.company?.name, row.product?.productName].filter(Boolean).join(' — ');
  const balanced = allQuantitiesValid && allocated === total;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="xl"
        dismissOnBackdrop={false}
        title={alreadySplit ? 'Edit the split' : 'Split this deliverable'}
        description={rowName || undefined}
        footer={
          <>
            {blockedReason && (
              <span className="self-center text-xs text-fg-muted sm:mr-auto">{blockedReason}</span>
            )}
            {alreadySplit && (
              <Button
                variant="secondary"
                onClick={() => {
                  setUnsplitError(null);
                  setConfirmUnsplit(true);
                }}
                disabled={saving || target === null}
              >
                Unsplit
              </Button>
            )}
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void save()}
              loading={saving}
              disabled={blockedReason !== null}
            >
              Save split
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {formError && (
            <Notice tone="danger" role="alert" className="break-words">
              {formError}
            </Notice>
          )}

          <p
            role="status"
            aria-live="polite"
            className={
              balanced ? 'text-sm font-medium text-fg' : 'text-sm font-medium text-warning'
            }
          >
            {allocated} of {total} allocated
          </p>

          <ol className="space-y-3">
            {drafts.map((draft, index) => (
              <li key={draft.localKey} className="rounded-md border border-border p-3">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-fg">Piece {index + 1}</h3>
                  {drafts.length > 2 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removePiece(draft.localKey)}
                      disabled={saving}
                    >
                      Remove piece
                    </Button>
                  )}
                </div>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Field label="Quantity" error={errors[`${draft.localKey}.promised`]}>
                    {({ id, 'aria-describedby': describedBy }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        inputMode="numeric"
                        autoComplete="off"
                        value={draft.promised}
                        disabled={saving}
                        invalid={Boolean(errors[`${draft.localKey}.promised`])}
                        onChange={(event) => setField(draft.localKey, 'promised', event.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Completed" error={errors[`${draft.localKey}.completed`]}>
                    {({ id, 'aria-describedby': describedBy }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        inputMode="numeric"
                        autoComplete="off"
                        value={draft.completed}
                        disabled={saving}
                        invalid={Boolean(errors[`${draft.localKey}.completed`])}
                        onChange={(event) => setField(draft.localKey, 'completed', event.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Start date" error={errors[`${draft.localKey}.startDate`]}>
                    {({ id, 'aria-describedby': describedBy }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        type="date"
                        value={draft.startDate}
                        disabled={saving}
                        invalid={Boolean(errors[`${draft.localKey}.startDate`])}
                        onChange={(event) => setField(draft.localKey, 'startDate', event.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="End date" error={errors[`${draft.localKey}.endDate`]}>
                    {({ id, 'aria-describedby': describedBy }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        type="date"
                        value={draft.endDate}
                        min={draft.startDate || undefined}
                        disabled={saving}
                        invalid={Boolean(errors[`${draft.localKey}.endDate`])}
                        onChange={(event) => setField(draft.localKey, 'endDate', event.target.value)}
                      />
                    )}
                  </Field>

                  <div className="sm:col-span-2">
                    <Field
                      label="Cities"
                      error={
                        errors[`${draft.localKey}.cityIds`] ??
                        (citiesFailed ? 'The cities could not be loaded.' : undefined)
                      }
                    >
                      {({ id, 'aria-describedby': describedBy }) => (
                        <MultiCombobox
                          id={id}
                          aria-describedby={describedBy}
                          values={draft.cityIds}
                          onChange={(next) => setField(draft.localKey, 'cityIds', next)}
                          options={cityOptions}
                          placeholder="The row's cities"
                          searchPlaceholder="Filter cities…"
                          loading={cities === null}
                          disabled={saving || citiesFailed}
                          invalid={Boolean(errors[`${draft.localKey}.cityIds`])}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field
                      label="Regions"
                      error={
                        errors[`${draft.localKey}.regionIds`] ??
                        (regions.failed ? 'The regions could not be loaded.' : undefined)
                      }
                    >
                      {({ id, 'aria-describedby': describedBy }) => (
                        <MultiCombobox
                          id={id}
                          aria-describedby={describedBy}
                          values={draft.regionIds}
                          onChange={(next) => setField(draft.localKey, 'regionIds', next)}
                          options={regionOptions}
                          placeholder="The row's regions"
                          searchPlaceholder="Filter regions…"
                          loading={regions.loading}
                          disabled={saving || regions.failed}
                          invalid={Boolean(errors[`${draft.localKey}.regionIds`])}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          {drafts.length < MAX_PIECES && (
            <Button variant="secondary" onClick={addPiece} disabled={saving}>
              Add piece
            </Button>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmUnsplit}
        onClose={() => {
          if (!saving) setConfirmUnsplit(false);
        }}
        onConfirm={() => void unsplit()}
        title="Unsplit this deliverable?"
        description={`The ${storedPieceCount} pieces are removed and this becomes one row again. Their completed counts are added together and become the row's completed count (${completedSum}). Dates, cities and regions set on the pieces are not kept.`}
        confirmLabel="Unsplit"
        tone="danger"
        loading={saving}
        error={unsplitError}
      />
    </>
  );
}
